"use client"

import type { Organization } from "better-auth/plugins/organization"
import { MenuDots as EllipsisIcon, Refresh as Loader2, Logout2 as LogOutIcon, Settings as SettingsIcon } from "@papier/ui/components/icons"
import { useCallback, useContext, useMemo, useState } from "react"

import { AuthUIContext } from "../../lib/auth-ui-provider"
import { getLocalizedError } from "../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { Button } from "@papier/ui/components/ui/button"
import { Card } from "@papier/ui/components/ui/card"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@papier/ui/components/ui/dropdown-menu"
import { LeaveOrganizationDialog } from "./leave-organization-dialog"
import { OrganizationCellView } from "./organization-cell-view"
import { useQueryClient } from "@tanstack/react-query"

export interface OrganizationCellProps {
    className?: string
    classNames?: SettingsCardClassNames
    organization: Organization
    localization?: AuthLocalization
    onLeaveOrganizationSuccess?: () => Promise<void>
}

export function OrganizationCell({
    className,
    classNames,
    organization,
    localization: localizationProp,
    onLeaveOrganizationSuccess
}: OrganizationCellProps) {
    const {
        authClient,
        localization: contextLocalization,
        organization: organizationOptions,
        navigate,
        toast
    } = useContext(AuthUIContext)
    const queryClient = useQueryClient()

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const { pathMode } = organizationOptions || {}

    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false)
    const [isManagingOrganization, setIsManagingOrganization] = useState(false)

    const handleManageOrganization = useCallback(async () => {
        setIsManagingOrganization(true)

        if (pathMode === "slug") {
            navigate(
                `${organizationOptions?.basePath}/${organization.slug}/${organizationOptions?.viewPaths.SETTINGS}`
            )

            return
        }

        try {
            await authClient.organization.setActive({
                organizationId: organization.id,
                fetchOptions: {
                    throw: true
                }
            })
            void queryClient.resetQueries()

            navigate(
                `${organizationOptions?.basePath}/${organizationOptions?.viewPaths?.SETTINGS}`
            )
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })

            setIsManagingOrganization(false)
        }
    }, [
        authClient,
        queryClient,
        organization.id,
        organizationOptions?.basePath,
        organizationOptions?.viewPaths?.SETTINGS,
        organization.slug,
        pathMode,
        navigate,
        toast,
        localization
    ])

    return (
        <>
            <Card
                className={cn(
                    "min-w-0 w-full flex-row items-center gap-3 p-4",
                    className,
                    classNames?.cell
                )}
            >
                <OrganizationCellView
                    className="min-w-0 flex-1"
                    organization={organization}
                    localization={localization}
                />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            aria-label={`${localization.ORGANIZATION}: ${organization.name}`}
                            className={cn(
                                "relative ms-auto shrink-0",
                                classNames?.button,
                                classNames?.outlineButton
                            )}
                            disabled={isManagingOrganization}
                            size="icon"
                            type="button"
                            variant="outline"
                        >
                            {isManagingOrganization ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                <EllipsisIcon className={classNames?.icon} />
                            )}
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="start">
                        <DropdownMenuItem
                            onClick={() => void handleManageOrganization()}
                            disabled={isManagingOrganization}
                        >
                            <SettingsIcon className={classNames?.icon} />

                            {localization.MANAGE_ORGANIZATION}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            onClick={() => setIsLeaveDialogOpen(true)}
                            variant="destructive"
                        >
                            <LogOutIcon className={classNames?.icon} />

                            {localization.LEAVE_ORGANIZATION}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </Card>

            <LeaveOrganizationDialog
                open={isLeaveDialogOpen}
                onOpenChange={setIsLeaveDialogOpen}
                organization={organization}
                localization={localization}
                onLeaveOrganizationSuccess={onLeaveOrganizationSuccess}
            />
        </>
    )
}
